'use client'

import React from 'react'
import { DafitiBlack } from '../../brands'
import { Back } from '../../icons'
import { cn, warnIfEmptyLabel } from '../../utils'

export type HeaderMinimalRootProps = React.ComponentProps<'header'>

/**
 * Minimal variant of the header, used on focused flows such as checkout where
 * navigation must be reduced to the brand and an optional way back.
 *
 * The grid keeps the brand optically centered whether or not
 * `HeaderMinimalBack` is rendered, since the leading and trailing tracks are
 * both `1fr`.
 */
export const HeaderMinimalRoot = ({
  children,
  className,
  ...props
}: HeaderMinimalRootProps) => {
  return (
    <header
      {...props}
      className={cn(
        'relative border-b-[1px] border-gray-200 bg-white lg:border-gray-300',
        className,
      )}
    >
      <div className="container grid min-h-16 grid-cols-[1fr_auto_1fr] items-center lg:min-h-24">
        {children}
      </div>
    </header>
  )
}

export type HeaderMinimalBackProps<T extends React.ElementType = 'button'> = {
  /**
   * Element the action renders as. Defaults to `button`; pass `a` (or a router
   * link component) when the destination is a real URL.
   */
  as?: T
  /**
   * Visible text of the button. Hidden below `lg`, where the button collapses
   * into an icon-only tap target, and used as the accessible name.
   */
  label: string
  /** Overrides the accessible name when it must differ from `label`. */
  ariaLabel?: string
  /**
   * Blocks the action. Elements other than `<button>` get `aria-disabled` and
   * are dropped from the tab order instead, since they ignore `disabled`.
   */
  disabled?: boolean
} & React.ComponentPropsWithoutRef<T>

/**
 * Back action for the minimal header. Render it only on the screens that have
 * somewhere to go back to — the brand stays centered when it is omitted.
 */
export const HeaderMinimalBack = <T extends React.ElementType>({
  as,
  label,
  ariaLabel,
  className,
  disabled,
  ...props
}: HeaderMinimalBackProps<T>) => {
  warnIfEmptyLabel(ariaLabel || label, 'HeaderMinimalBack: label')

  const Comp = as || 'button'
  const isNativeButton = Comp === 'button'

  // Elements other than `<button>` ignore `disabled`, so fall back to the
  // accessible equivalent and drop the element out of the tab order. Mirrors
  // the contract `Button` already exposes.
  const disabledProps =
    !isNativeButton && disabled
      ? { 'aria-disabled': true, tabIndex: -1 }
      : { disabled }

  return (
    <Comp
      type={isNativeButton ? 'button' : undefined}
      {...props}
      {...disabledProps}
      aria-label={ariaLabel || label}
      className={cn(
        'gap-quark col-start-1 inline-flex size-12 cursor-pointer items-center justify-center justify-self-start rounded-full text-gray-600 transition-all',
        'text-3xs font-medium hover:bg-gray-100 active:bg-gray-200 disabled:pointer-events-none disabled:text-gray-400 aria-disabled:pointer-events-none aria-disabled:text-gray-400',
        'lg:py-nano lg:pl-3xs lg:pr-2xs lg:size-auto lg:min-h-10 lg:bg-white',
        className,
      )}
    >
      <Back
        aria-hidden="true"
        className="lg:w-1.75 h-5 w-2.5 shrink-0 lg:h-3.5"
      />
      <span className="hidden lg:inline">{label}</span>
    </Comp>
  )
}

export type HeaderMinimalBrandProps<T extends React.ElementType = 'a'> = {
  /**
   * Element the brand renders as. Defaults to `a`; pass a router link
   * component to keep client-side navigation.
   */
  as?: T
  ariaLabel: string
} & React.ComponentPropsWithoutRef<T>

/**
 * Brand of the minimal header. Renders the Dafiti logo unless a different one
 * is passed as children.
 */
export const HeaderMinimalBrand = <T extends React.ElementType>({
  as,
  ariaLabel,
  className,
  children,
  ...props
}: HeaderMinimalBrandProps<T>) => {
  warnIfEmptyLabel(ariaLabel, 'HeaderMinimalBrand: ariaLabel')

  const Comp = as || 'a'

  return (
    <Comp
      {...props}
      aria-label={ariaLabel}
      className={cn('col-start-2 flex items-center justify-center', className)}
    >
      {children || <DafitiBlack className="lg:w-25 w-20" />}
    </Comp>
  )
}
